import React from 'react';
import {
  ProposalSection, ProposalColumn, ProposalRichText, ProposalTitle,
  ProposalImage, ProposalShape, ProposalCheckbox, ProposalSelector,
} from '..';
import { EditableDataTable } from '../../web';
import { puckProposalSectionColumnsField, puckSectionColumnsSettingsField } from '../../../puck/fields/puckProposalSectionColumnsField';
import { DEFAULT_SECTION_COLUMNS, DEFAULT_STACK_AT_PX } from '../../../utils/proposalSectionColumns';
import { DEFAULT_SECTION_MARGIN_BOTTOM_CSS, pxToRemCss } from '../../../utils/proposalUnits';
import { PAGE_CONTENT_GUTTER_PX } from '../../../layout/pageLayoutConstants';
import { puckColorField } from '../../../puck/fields/createPuckColorField';
import { puckDebouncedTextField } from '../../../puck/fields/puckDebouncedTextField';
import { slugifyOrEmpty } from '../../../parser/proposalToWebData';
import { PxSpacingInput, puckPxSpacingField } from '../../../puck/fields/puckPxSpacingField';
import { puckVisibilityField } from '../../../puck/fields/puckVisibilityField';
import AppearAnimationField, { puckAppearAnimationDurationField } from '../../../puck/fields/AppearAnimationField';
import SectionPaddingYField from './SectionPaddingYField';
import SectionBackgroundMediaField from './SectionBackgroundMediaField';
import { APPEAR_ANIMATION_DURATION_DEFAULT_MS } from '../../../utils/appearAnimations';
import { sectionFieldsForParent } from './nestedSectionFields';
import { DEFAULT_BACKGROUND_OVERLAY_OPACITY } from './ProposalSection';
import { puckAdvancedSectionField, puckSectionField } from '../../../puck/fields/puckSectionField';

/** Gouttière historique du Bloc, désormais réglable. Doit rester la valeur
 *  de repli de ProposalSection quand la prop est absente. */
const DEFAULT_SECTION_PADDING_X_CSS = pxToRemCss(PAGE_CONTENT_GUTTER_PX);

// Map des composants blocs (ProposalColumn.blocks)
const BLOCK_COMPONENTS = {
  ProposalRichText,
  ProposalTitle,
  ProposalImage,
  ProposalShape,
  EditableDataTable,
  ProposalCheckbox,
  ProposalSelector,
};

/**
 * Rend un tableau de descripteurs de blocs Puck (ex: ProposalColumn.blocks)
 * en éléments React. Les descripteurs sont des objets JSON { type, props } —
 * pas des éléments React — donc React.cloneElement ne peut pas être utilisé.
 */
const renderBlocks = (items) => {
  if (!items || !Array.isArray(items)) return null;
  return items.map((item, i) => {
    if (!item?.type) return null;
    const Component = BLOCK_COMPONENTS[item.type];
    if (!Component) return null;
    return <Component key={item.props?.id || i} {...item.props} />;
  });
};

/**
 * Rend un tableau de descripteurs ProposalColumn en éléments React.
 */
const renderColumns = (items) => {
  if (!items || !Array.isArray(items)) return null;
  return items.map((item, i) => {
    if (item?.type !== 'ProposalColumn') return null;
    return (
      <ProposalColumn
        key={item.props?.id || i}
        {...item.props}
        blocks={renderBlocks(item.props?.blocks)}
      />
    );
  });
};

const proposalSectionConfig = {
  label: 'Bloc',
  defaultProps: {
    backgroundColor: '',
    backgroundMedia: '',
    mediaFit: 'cover',
    backgroundOverlayColor: '',
    backgroundOverlayOpacity: DEFAULT_BACKGROUND_OVERLAY_OPACITY,
    paddingTop: 0,
    paddingBottom: 0,
    paddingX: DEFAULT_SECTION_PADDING_X_CSS,
    marginBottom: DEFAULT_SECTION_MARGIN_BOTTOM_CSS,
    // 16 px explicites : le champ affiche la valeur effective ; vidé ('') = 0.
    columnGap: '1rem',
    rowGap: '1rem',
    // Empilement des colonnes : viewport ≤ px (768 = mobile+tablette, 0 = jamais).
    stackAtPx: DEFAULT_STACK_AT_PX,
    isVisible: true,
    /** Nom d'ancre lisible ; vide = le bloc reste ciblable par son id. */
    anchor: '',
    appearAnimation: 'none',
    appearAnimationDuration: APPEAR_ANIMATION_DURATION_DEFAULT_MS,
    columns: [],
    sectionColumns: { ...DEFAULT_SECTION_COLUMNS },
    /** Dernière disposition personnalisée (tuile « Personnalisé » du picker). */
    savedCustomLayout: null,
  },
  fields: {
    isVisible: puckVisibilityField(),

    _section1: puckSectionField('Mise en page'),
    sectionColumns: puckProposalSectionColumnsField('Colonnes'),
    /* Juste sous la disposition : l'ancre est l'identité du bloc, pas un
       réglage de finition. Reléguée en fin de sidebar, elle se cherchait après
       les marges. Sans ancre nommée, un bouton qui vise ce bloc doit pointer
       son id (`section-hero-a3f9c1`) : ça marche, mais l'URL est illisible. */
    paddingTop: {
      type: 'custom',
      label: 'Marge verticale',
      render: (props) => <SectionPaddingYField {...props} />,
    },
    paddingX: {
      type: 'custom',
      label: 'Marge horizontale',
      render: ({ value, onChange }) => (
        <PxSpacingInput
          value={value === undefined ? DEFAULT_SECTION_PADDING_X_CSS : value}
          onChange={onChange}
          label="Marge horizontale"
          description="0 = le contenu prend toute la largeur (bandeau, carrousel…)."
        />
      ),
    },
    marginBottom: puckPxSpacingField('Espace sous le bloc'),

    _section2: puckSectionField('Style'),
    backgroundColor: puckColorField('Couleur de fond', { allowTransparent: true }),
    backgroundMedia: {
      type: 'custom',
      label: 'Média de fond',
      render: (props) => <SectionBackgroundMediaField {...props} />,
    },

    _section3: puckSectionField('Affichage'),
    anchor: puckDebouncedTextField('Ancre', {
      placeholder: 'nos-tarifs',
      description: 'Nom court pour cibler ce bloc depuis un bouton ou un lien.',
      /* `slugify` et pas `slugifyOrEmpty` était un bug : elle se termine par
         un repli `|| 'page'`, écrit pour les slugs de page où le vide est
         interdit. Vider le champ y écrivait donc `page`, et tous les blocs
         finissaient par porter la même ancre. Ici le vide est la valeur
         normale : sans ancre, le bloc se vise par son id. */
      transform: slugifyOrEmpty,
    }),

    _section4: puckSectionField('Animation'),
    appearAnimation: {
      type: 'custom',
      label: "Animation d'apparition",
      render: ({ value, onChange }) => (
        <AppearAnimationField value={value} onChange={onChange} label="Animation d'apparition" />
      ),
    },
    /* Les Blocs enregistrés avant ce champ n'ont pas la prop : le champ
       afficherait « vide » (donc 0) alors que la gouttière de 16 px est bien
       appliquée. On affiche la valeur effective, pas la valeur stockée. */

    _section5: puckAdvancedSectionField(),
    _columnsSettings: puckSectionColumnsSettingsField(),
    appearAnimationDuration: puckAppearAnimationDurationField(),
  },
  /* Dans un bloc hôte (un onglet), le Bloc ne touche plus la page : ses
     réglages de proximité disparaissent, cf. nestedSectionFields.js. */
  resolveFields: (data, { fields, parent }) => sectionFieldsForParent(fields, parent),
  render: ({
    id,
    backgroundColor,
    backgroundMedia,
    mediaFit,
    backgroundOverlayColor,
    backgroundOverlayOpacity,
    paddingTop,
    paddingBottom,
    paddingX,
    marginBottom,
    columnGap,
    rowGap,
    stackAtPx,
    columnStyles,
    columnsJustify,
    isVisible,
    anchor,
    appearAnimation,
    appearAnimationDuration,
    columns,
    sectionColumns,
    columnIds,
    columnSpans,
    columnWidths,
    puck,
  }) => {
    const legacyNested = Array.isArray(columns) && columns.some((c) => c?.type === 'ProposalColumn');
    return (
      <ProposalSection
        id={id}
        backgroundColor={backgroundColor}
        backgroundMedia={backgroundMedia}
        mediaFit={mediaFit}
        backgroundOverlayColor={backgroundOverlayColor}
        backgroundOverlayOpacity={backgroundOverlayOpacity}
        paddingTop={paddingTop}
        paddingBottom={paddingBottom}
        paddingX={paddingX}
        marginBottom={marginBottom}
        columnGap={columnGap}
        rowGap={rowGap}
        stackAtPx={stackAtPx}
        columnStyles={columnStyles}
        columnsJustify={columnsJustify}
        isVisible={isVisible}
        anchor={anchor}
        appearAnimation={appearAnimation}
        appearAnimationDuration={appearAnimationDuration}
        legacyNested={legacyNested}
        columns={legacyNested ? renderColumns(columns) : null}
        sectionColumns={sectionColumns}
        columnIds={columnIds}
        columnSpans={columnSpans}
        columnWidths={columnWidths}
        puck={puck}
      />
    );
  },
};

export default proposalSectionConfig;
